import { Stack, Typography } from "@mui/material"

import {
  CourseLanguage,
  CourseSyllabusLiveEvent,
  Topic,
} from "@masterschool/course-builder-api"
import useSelectedLiveEvent from "./custom-hooks/useSelectedLiveEvent"
import { selectPublishValidationsForItem } from "../../features/courseEditor/courseValidationsSelectors"
import useEditLiveEvent from "./custom-hooks/useEditLiveEvent"
import { useAppSelector } from "@app/hooks"
import TimeEstimationTextField from "../components/timeEstimationTextField"
import MultiLanguageUrlTextField from "../components/multiLanguageFields/multipleLanguageUrlTextField"
import { MultiLanguageTitleTextField } from "../components/multiLanguageFields/multiLanguageTitleTextField"
import { MultiLanguageEditorTextField } from "../components/multiLanguageFields/multiLanguageEditorTextField"

export function LiveEventEditor(props: { topic: Topic }) {
  const { topic } = props
  const liveEvent = useSelectedLiveEvent()
  const editLiveEvent = useEditLiveEvent()
  const errors = useAppSelector(selectPublishValidationsForItem(liveEvent?.id))
  const editingLanguage = useAppSelector(
    (state) => state.courseEditor.editingLanguage,
  )

  if (!liveEvent) {
    return undefined
  }

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "white",
        alignItems: "center",
        gap: "32px",
        padding: "24px",
      }}
    >
      <StudentFacingInfo
        liveEvent={liveEvent}
        topic={topic}
        errors={errors || []}
        editLiveEvent={editLiveEvent}
        editingLanguage={editingLanguage}
      />
      <InstructorFacingInfo
        liveEvent={liveEvent}
        topic={topic}
        errors={errors || []}
        editLiveEvent={editLiveEvent}
        editingLanguage={editingLanguage}
      />
      <Settings
        liveEvent={liveEvent}
        topic={topic}
        editLiveEvent={editLiveEvent}
      />
    </Stack>
  )
}

function StudentFacingInfo({
  liveEvent,
  topic,
  errors,
  editingLanguage,
  editLiveEvent,
}: {
  liveEvent: CourseSyllabusLiveEvent
  topic: Topic
  errors: string[]
  editingLanguage: CourseLanguage
  editLiveEvent: ReturnType<typeof useEditLiveEvent>
}) {
  const isPresentationUrlInvalid =
    errors.includes("INVALID_PRESENTATION_URL") && !!liveEvent?.presentationUrl
  const isMaterialsUrlInvalid =
    errors.includes("INVALID_MATERIALS_URL") && !!liveEvent?.materialsUrl

  return (
    <Stack
      sx={{
        width: "100%",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <Typography variant="body2_sb" sx={{ alignSelf: "left", width: "100%" }}>
        Student facing
      </Typography>
      <MultiLanguageTitleTextField
        elementId={liveEvent.id}
        defaultLanguageValue={liveEvent.title}
        extraLanguageValueRecord={liveEvent.extraLanguageTitleRecord}
        language={editingLanguage}
        debounce
        label="Session name"
        onChangeDefaultLanguage={(value) => {
          editLiveEvent(liveEvent, "title", value, topic)
        }}
        onChangeExtraLanguage={(value) => {
          editLiveEvent(liveEvent, "extraLanguageTitleRecord", value, topic)
        }}
      />
      <MultiLanguageEditorTextField
        defaultLanguageValue={liveEvent.description}
        extraLanguageValueRecord={liveEvent.extraLanguageDescriptionRecord}
        language={editingLanguage}
        debounce
        label="Description"
        onChangeDefaultLanguage={(value) => {
          editLiveEvent(liveEvent, "description", value, topic)
        }}
        onChangeExtraLanguage={(value) => {
          editLiveEvent(
            liveEvent,
            "extraLanguageDescriptionRecord",
            value,
            topic,
          )
        }}
        rows={3}
        multiline
        fullWidth
        size="small"
        InputProps={{ sx: { fontSize: "14px", padding: "16px" } }}
        InputLabelProps={{ sx: { fontSize: "14px" } }}
      />
      <MultiLanguageUrlTextField
        elementId={liveEvent.id}
        defaultLanguageValue={liveEvent.presentationUrl}
        extraLanguageValueRecord={liveEvent.extraLanguagePresentationUrlRecord}
        language={editingLanguage}
        label="Presentation URL (optional)"
        invalidUrl={isPresentationUrlInvalid}
        debounce
        onChangeDefaultLanguage={(value) => {
          editLiveEvent(liveEvent, "presentationUrl", value, topic)
        }}
        onChangeExtraLanguage={(value) => {
          editLiveEvent(
            liveEvent,
            "extraLanguagePresentationUrlRecord",
            value,
            topic,
          )
        }}
      />
      <MultiLanguageUrlTextField
        elementId={liveEvent.id}
        defaultLanguageValue={liveEvent.materialsUrl}
        extraLanguageValueRecord={liveEvent.extraLanguageMaterialsUrlRecord}
        language={editingLanguage}
        label="Materials URL (optional)"
        invalidUrl={isMaterialsUrlInvalid}
        debounce
        onChangeDefaultLanguage={(value) => {
          editLiveEvent(liveEvent, "materialsUrl", value, topic)
        }}
        onChangeExtraLanguage={(value) => {
          editLiveEvent(
            liveEvent,
            "extraLanguageMaterialsUrlRecord",
            value,
            topic,
          )
        }}
      />
    </Stack>
  )
}

function InstructorFacingInfo({
  liveEvent,
  topic,
  errors,
  editingLanguage,
  editLiveEvent,
}: {
  liveEvent: CourseSyllabusLiveEvent
  topic: Topic
  errors: string[]
  editingLanguage: CourseLanguage
  editLiveEvent: ReturnType<typeof useEditLiveEvent>
}) {
  const isLessonPlanUrlMissing =
    errors.includes("MISSING_LESSON_PLAN_URL") && !liveEvent?.lessonPlanUrl
  const isLessonPlanUrlInvalid =
    errors.includes("INVALID_LESSON_PLAN_URL") && !!liveEvent?.lessonPlanUrl

  return (
    <Stack
      sx={{
        width: "100%",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <Typography variant="body2_sb" sx={{ alignSelf: "left", width: "100%" }}>
        Instructor facing
      </Typography>
      <MultiLanguageUrlTextField
        elementId={liveEvent.id}
        defaultLanguageValue={liveEvent.lessonPlanUrl}
        extraLanguageValueRecord={liveEvent.extraLanguageLessonPlanUrlRecord}
        language={editingLanguage}
        debounce
        missingUrl={isLessonPlanUrlMissing}
        invalidUrl={isLessonPlanUrlInvalid}
        label="Lesson plan URL"
        onChangeDefaultLanguage={(value) => {
          editLiveEvent(liveEvent, "lessonPlanUrl", value, topic)
        }}
        onChangeExtraLanguage={(value) => {
          editLiveEvent(
            liveEvent,
            "extraLanguageLessonPlanUrlRecord",
            value,
            topic,
          )
        }}
      />
    </Stack>
  )
}

function Settings({
  liveEvent,
  topic,
  editLiveEvent,
}: {
  liveEvent: CourseSyllabusLiveEvent
  topic: Topic
  editLiveEvent: ReturnType<typeof useEditLiveEvent>
}) {
  return (
    <Stack
      sx={{
        width: "100%",
        alignItems: "center",
        gap: "16px",
      }}
    >
      <Typography variant="body2_sb" sx={{ alignSelf: "left", width: "100%" }}>
        Settings
      </Typography>
      <TimeEstimationTextField
        elementId={liveEvent.id}
        value={liveEvent.estimatedDuration}
        debounce
        onChange={(e) => {
          editLiveEvent(
            liveEvent,
            "estimatedDuration",
            parseInt(e.target.value),
            topic,
          )
        }}
      />
    </Stack>
  )
}
